import React from 'react';
import { Car, CheckCircle, Fuel, Shield, ChevronRight, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const LocationToyotaCHR = () => {
  const phoneNumber = "0650972620";
  const telLink = "tel:+33650972620";

  const handleCallClick = (e) => {
    if (window.innerWidth >= 768) {
      e.preventDefault();
      alert(`Appelez-nous au ${phoneNumber.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')}`);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-sm z-50 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Car className="w-8 h-8 text-green-500" />
              <span className="text-xl font-bold">Location VTC Île-de-France</span>
            </Link>
            <div className="flex items-center gap-3">
              <a 
                href={telLink} 
                onClick={handleCallClick}
                className="hidden sm:inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 px-6 py-2 rounded-full font-semibold transition"
              >
                <Phone className="w-4 h-4" />
                Appeler
              </a>
              <Link to="/#contact" className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full font-semibold transition border border-white/20">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Floating Call Button (Mobile Only) */}
      <a
        href={telLink}
        className="sm:hidden fixed bottom-6 right-6 z-40 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110 flex items-center justify-center"
        aria-label="Appeler maintenant"
      >
        <Phone className="w-6 h-6" />
      </a>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Location Voiture VTC <span className="text-green-500">Toyota C-HR</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-8">
            Le véhicule hybride idéal pour votre activité de chauffeur VTC en Île-de-France
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-invert max-w-none">
            <h2 className="text-3xl font-bold mb-6 text-white">Toyota C-HR : Le choix parfait pour la location VTC</h2>
            <p className="text-gray-300 text-lg mb-6">
              Le <strong>Toyota C-HR hybride</strong> est devenu le véhicule de référence pour la <strong>location voiture VTC</strong> en Île-de-France. Avec sa technologie hybride, son design moderne et sa fiabilité légendaire, c'est le choix idéal pour les chauffeurs VTC professionnels.
            </p>

            {/* Image */}
            <div className="my-12 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1628188869280-0210bf9b2dc2" 
                alt="Toyota C-HR pour location VTC"
                className="w-full h-96 object-cover"
              />
            </div>

            <h3 className="text-2xl font-bold mb-4 text-white mt-12">Pourquoi choisir le Toyota C-HR pour votre activité VTC ?</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                <Fuel className="w-10 h-10 text-green-500 mb-4" />
                <h4 className="font-bold text-xl mb-3">Économie de carburant</h4>
                <p className="text-gray-400">Consommation moyenne de seulement <strong className="text-green-500">4.2L/100km</strong> grâce à la technologie hybride. Réduisez considérablement vos coûts d'exploitation.</p>
              </div>

              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                <Shield className="w-10 h-10 text-green-500 mb-4" />
                <h4 className="font-bold text-xl mb-3">Fiabilité Toyota</h4>
                <p className="text-gray-400">La réputation de <strong className="text-green-500">fiabilité Toyota</strong> n'est plus à faire. Moins de pannes, moins de temps d'immobilisation.</p>
              </div>

              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
                <h4 className="font-bold text-xl mb-3">Confort passagers</h4>
                <p className="text-gray-400">Intérieur spacieux et confortable. Vos clients apprécieront le trajet dans ce véhicule premium.</p>
              </div>

              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                <Car className="w-10 h-10 text-green-500 mb-4" />
                <h4 className="font-bold text-xl mb-3">Design moderne</h4>
                <p className="text-gray-400">Apparence élégante et professionnelle qui valorise votre image de chauffeur VTC.</p>
              </div>
            </div>

            <h3 className="text-2xl font-bold mb-4 text-white mt-12">Caractéristiques techniques du Toyota C-HR</h3>
            <div className="bg-gray-800/50 p-8 rounded-xl border border-gray-700 my-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-gray-400 mb-2">Motorisation</p>
                  <p className="font-bold text-lg">Hybride essence-électrique</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-2">Consommation</p>
                  <p className="font-bold text-lg text-green-500">4.2L/100km</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-2">Émissions CO2</p>
                  <p className="font-bold text-lg">Faibles émissions</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-2">Places</p>
                  <p className="font-bold text-lg">5 places confortables</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-2">Coffre</p>
                  <p className="font-bold text-lg">377 litres</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-2">Transmission</p>
                  <p className="font-bold text-lg">Automatique CVT</p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold mb-4 text-white mt-12">Location Toyota C-HR VTC : Nos avantages</h3>
            <ul className="space-y-4 text-gray-300 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Kilométrage illimité</strong> - Roulez autant que nécessaire pour maximiser vos revenus</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Sans caution</strong> - Démarrez immédiatement sans bloquer de capital</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Entretien inclus</strong> - Maintenance et révisions prises en charge</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Véhicules récents</strong> - Flotte régulièrement renouvelée</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Compatible toutes plateformes</strong> - Uber, Bolt, Heetch, FreeNow</span>
              </li>
            </ul>

            <h3 className="text-2xl font-bold mb-4 text-white mt-12">Rentabilité : Calculez vos économies</h3>
            <div className="bg-green-500/10 border border-green-500 rounded-xl p-8 my-8">
              <p className="text-gray-300 mb-4">
                Avec une consommation de seulement <strong className="text-green-500">4.2L/100km</strong>, le Toyota C-HR hybride vous permet d'économiser jusqu'à <strong className="text-green-500">40% sur vos coûts de carburant</strong> par rapport à un véhicule essence classique.
              </p>
              <p className="text-gray-300">
                Pour un chauffeur VTC parcourant 200km par jour, cela représente une économie de <strong className="text-green-500">plusieurs centaines d'euros par mois</strong>.
              </p>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-8 my-12 text-center">
              <h3 className="text-2xl font-bold mb-4">Prêt à louer votre Toyota C-HR VTC ?</h3>
              <p className="text-gray-300 mb-6">Contactez-nous pour obtenir votre véhicule rapidement</p>
              <Link to="/#contact" className="inline-flex items-center bg-green-500 hover:bg-green-600 px-8 py-4 rounded-full font-semibold text-lg transition">
                Demander un véhicule <ChevronRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center space-x-2 mb-4">
            <Car className="w-6 h-6 text-green-500" />
            <span className="font-bold">Location VTC Île-de-France</span>
          </Link>
          <p className="text-gray-400 text-sm">
            Location voiture VTC Toyota C-HR | Location VTC hybride | Véhicules économiques pour chauffeurs
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LocationToyotaCHR;
